import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { deleteCompanyErrorNotification, addCompanyErrorNotification, loadAllUsers } from '../api'
import { CompanyErrorNotificationModel } from 'src/Libs/company-settings/models'

interface UseCompanyErrorNotificationHook {
  /**
   * Toggle the error notification status for a single user, on a single company
   *
   * @param companyId
   * @param userId
   * @param notificationId
   * @param targetStateActive
   */
  toggleStatus(companyId: number, userId: number, notificationId: number | null, targetStateActive: boolean): Promise<number | null>
  loadAll(companyId: number): Promise<CompanyErrorNotificationModel[]>
}

/**
 * Provides the ability to update a companies profile settings as a cumulated update
 */
const useCompanyErrorNotification = (): UseCompanyErrorNotificationHook => {
  const { t } = useCustomTranslation()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const authFetch = useAuthFetchWithResponse()

  const toggleStatus = async (
    companyId: number,
    userId: number,
    notificationId: number | null,
    targetStateActive: boolean
  ): Promise<number | null> => {
    return new Promise((resolve, reject) => {
      if (targetStateActive) {
        return addCompanyErrorNotification(companyId, userId, authFetch)
          .then(result => {
            if (result.hasErrors) {
              const message = t('user.company.errorNotificationSetting.add.error')
              enqueueErrorStack(message)
              reject(message)
            } else {
              enqueueSuccessStack(t('user.company.errorNotificationSetting.add.success'))
              resolve(result.value as number)
            }
          })
          .catch(error => {
            console.error(error)
            enqueueErrorStack(t('user.company.errorNotificationSetting.add.error'))
            reject(error)
          })
      }

      if (!targetStateActive && notificationId) {
        return deleteCompanyErrorNotification(companyId, notificationId, authFetch)
          .then(result => {
            if (result.hasErrors) enqueueErrorStack(t('user.company.errorNotificationSetting.remove.error'))
            else enqueueSuccessStack(t('user.company.errorNotificationSetting.remove.success'))
            resolve(null)
          })
          .catch(error => {
            console.error(error)
            enqueueErrorStack(t('user.company.errorNotificationSetting.remove.error'))
            reject(error)
          })
      }
      const error = t('user.company.errorNotificationSetting.common.error')
      enqueueErrorStack(error)
      reject(error)
    })
  }

  const loadAll = async (companyId: number): Promise<CompanyErrorNotificationModel[]> => {
    return (await loadAllUsers(companyId, authFetch)).value || []
  }

  return { toggleStatus, loadAll }
}

export default useCompanyErrorNotification
